import React from "react";
import {
  Card,
  Flex,
  Typography,
  Input,
  Form,
  Row,
  Col,
  Select,
  Button,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import { useDispatch, useSelector } from "react-redux";
import { countries } from "../../../../../../../../shared-components/countries";
import DownloadDocIcon from "../../../../../../../../Icons/DownloadDocIcon";
import { cities } from "../../../../../../../../shared-components/cities";
import {
  calendarOptions,
  dateFormatOptions,
  firstDayOptions,
  measurementOptions,
  numberFormatOptions,
  temperatureOptions,
  timeFormatOptions,
} from "../../../DummyData";
import {
  updateDimensionAction,
  getSingleDimensionAction,
} from "../../../../../../../../store/action/admin/dimensionsEntityActions";
import { message } from "antd";

const { Text } = Typography;

const BranchSettingsS2 = React.forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const themeDefault = useSelector((state) => state.themeDefault);
  const getSingleDimensionList = useSelector(
    (state) => state.getSingleDimensionList
  );
  const dimensionId = getSingleDimensionList?.info?.dimension?._id;
  const [form] = Form.useForm();

  // Expose submit method to parent
  const submit = async () => {
    const values = await form.validateFields().catch(() => ({}));

    const addressParts = [
      values.address_name,
      values.address_line_1,
      values.address_line_2,
    ]
      .filter(Boolean)
      .join(", ");

    const payload = {
      type: "branch",
      branchLocation: {
        address: addressParts || undefined,
        city: values.city,
        state: values.state,
        postalCode: values.zip_code,
        country: values.country,
        latitude:
          values.latitude !== undefined &&
          values.latitude !== null &&
          values.latitude !== ""
            ? Number(values.latitude)
            : undefined,
        longitude:
          values.longitude !== undefined &&
          values.longitude !== null &&
          values.longitude !== ""
            ? Number(values.longitude)
            : undefined,
      },
      branchContact: {
        phone: values.phone_number,
        email: values.email,
        manager: values.contact_name,
        managerPhone: values.mobile_number,
        fax: values.fax,
      },
      internalNotes: values.internalNotes,
    };

    try {
      if (dimensionId) {
        await dispatch(updateDimensionAction(dimensionId, payload));
        message.success("Branch settings updated successfully");
        await dispatch(getSingleDimensionAction(dimensionId));
      }
    } catch (err) {
      // The action util already standardizes errors
    }

    return payload;
  };

  React.useImperativeHandle(ref, () => ({ submit }));

  // Watch selected country to enable city select
  const selectedCountry = Form.useWatch("country", form);
  const descriptionStyle = { fontSize: 12, color: "#A8A8A8", display: "block" };

  // Prefill values from API (mapping BE -> FE field names)
  React.useEffect(() => {
    const setting = getSingleDimensionList?.info?.setting || {};
    const branchLocation = setting.branch_location || {};
    const branchContact = setting.branch_contact || {};

    form.setFieldsValue({
      // Address
      address_name: branchLocation.address || undefined,
      address_line_1: undefined,
      address_line_2: undefined,
      country: branchLocation.country || undefined,
      city: branchLocation.city || undefined,
      zip_code: branchLocation.postalCode || undefined,
      latitude:
        branchLocation.latitude !== undefined &&
        branchLocation.latitude !== null
          ? String(branchLocation.latitude)
          : undefined,
      longitude:
        branchLocation.longitude !== undefined &&
        branchLocation.longitude !== null
          ? String(branchLocation.longitude)
          : undefined,

      // Contact
      contact_name: branchContact.manager || undefined,
      phone_number: branchContact.phone || undefined,
      mobile_number: branchContact.managerPhone || undefined,
      fax: branchContact.fax || undefined,
      email: branchContact.email || undefined,

      // Notes
      internalNotes: setting.internal_notes || undefined,
    });
  }, [getSingleDimensionList, form]);

  return (
    <Form form={form} layout="vertical">
      <Row gutter={[16, 16]}>
        <Col span={12}>
          {/* --- Location Details --- */}
          <Card
            title={
              <div
                style={{
                  borderBottom: themeDefault?.info?.newConfig?.cardborder,
                  paddingBottom: 8,
                }}
              >
                Location Details
              </div>
            }
            bordered
          >
            {/* --- Address Fields --- */}
            <Flex vertical gap={12}>
              <Form.Item
                name="address_name"
                label="Address Name"
                style={{ width: "100%", marginBottom: 0 }}
              >
                <Input placeholder="Enter Address Name" />
              </Form.Item>

              {/* <Form.Item
                name="address_line_1"
                label="Address Line 1"
                style={{ width: "100%", marginBottom: 0 }}
              >
                <Input placeholder="Enter Address Line 1" />
              </Form.Item>

              <Form.Item
                name="address_line_2"
                label="Address Line 2"
                style={{ width: "100%", marginBottom: 0 }}
              >
                <Input placeholder="Enter Address Line 2" />
              </Form.Item> */}
            </Flex>

            {/* --- Country & City --- */}
            <Flex gap={16} wrap="wrap" style={{ marginTop: 16 }}>
              <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                <Form.Item
                  name="country"
                  label="Country"
                  style={{ padding: 0, margin: 0 }}
                >
                  <Select
                    placeholder="Select Country"
                    showSearch
                    onChange={() => form.setFieldsValue({ city: undefined })}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={countries.map((c) => ({
                      value: c,
                      label: c,
                    }))}
                  />
                </Form.Item>
              </Flex>

              <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                <Form.Item
                  name="city"
                  label="City"
                  style={{ padding: 0, margin: 0 }}
                >
                  <Select
                    placeholder="Select City"
                    showSearch
                    disabled={!selectedCountry}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={(cities[selectedCountry] || []).map((c) => ({
                      value: c,
                      label: c,
                    }))}
                  />
                </Form.Item>
              </Flex>
            </Flex>

            {/* --- Coordinates --- */}
            <Flex gap={16} wrap="wrap" style={{ marginTop: 16 }}>
              <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                <Form.Item
                  name="longitude"
                  label="Longitude"
                  style={{ padding: 0, margin: 0 }}
                >
                  <Input placeholder="Enter Longitude" />
                </Form.Item>
              </Flex>

              <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                <Form.Item
                  name="latitude"
                  label="Latitude"
                  style={{ padding: 0, margin: 0 }}
                >
                  <Input placeholder="Enter Latitude" />
                </Form.Item>
              </Flex>
            </Flex>

            <Flex gap={16} wrap="wrap" style={{ marginTop: 16 }}>
              <Flex vertical style={{ minWidth: "49%" }}>
                <Form.Item
                  name="zip_code"
                  label="Zip Code"
                  style={{ padding: 0, margin: 0 }}
                >
                  <Input placeholder="Enter Zipcode" />
                </Form.Item>
              </Flex>
            </Flex>
          </Card>

          {/* <Card
            style={{ marginTop: 16 }}
            bordered
            title={
              <div
                style={{
                  borderBottom: themeDefault?.info?.newConfig?.cardborder,
                  paddingBottom: 8,
                }}
              >
                File Attachments
              </div>
            }
          >
            <Flex vertical gap={8}>
              <Text style={{ fontSize: 16, fontWeight: 700 }}>
                Upload a file(s) to link with this legal entity
              </Text>
              <Text type="secondary" style={{ fontSize: 12, paddingTop: 4 }}>
                Upload the relevant files for the legal entity
              </Text>
              <Button style={{ width: 150 }}>
                <DownloadDocIcon size="small" />
                Upload File
              </Button>
              <Text type="secondary" style={{ fontSize: 12, paddingTop: 4 }}>
                Only .xlsx, .xls, .csv files are supported
              </Text>
            </Flex>
          </Card> */}
        </Col>

        <Col span={12}>
          {/* --- Internal Notes --- */}
          <Card
            title={
              <div
                style={{
                  borderBottom: themeDefault?.info?.newConfig?.cardborder,
                  paddingBottom: 8,
                }}
              >
                Contact Details
              </div>
            }
            bordered
          >
            <Form.Item name="contact_name" label="Manager Name">
              <Input placeholder="Enter Manager Name" />
            </Form.Item>

            <Flex gap={16} wrap="wrap" style={{ marginTop: 16 }}>
              <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                <Form.Item
                  name="mobile_number"
                  label="Manager Phone Number"
                  style={{ padding: 0, margin: 0 }}
                >
                  <Input placeholder="Enter Manager Phone Number" />
                </Form.Item>
              </Flex>
            </Flex>

            <Flex gap={16} wrap="wrap" style={{ marginTop: 16 }}>
              <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                <Form.Item
                  name="phone_number"
                  label="Phone Number"
                  style={{ padding: 0, margin: 0 }}
                >
                  <Input placeholder="Enter Phone Number" />
                </Form.Item>
              </Flex>

              <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                <Form.Item
                  name="fax"
                  label="Fax"
                  style={{ padding: 0, margin: 0 }}
                >
                  <Input placeholder="Enter Fax" />
                </Form.Item>
              </Flex>
            </Flex>
          </Card>
          {/* --- Internal Notes --- */}
          <Card
            title={
              <div
                style={{
                  borderBottom: themeDefault?.info?.newConfig?.cardborder,
                  paddingBottom: 8,
                }}
              >
                Internal Notes (For Internal Use Only)
              </div>
            }
            style={{
              marginTop: 16,
            }}
            bordered
          >
            <Form.Item name="internalNotes" label="Notes">
              <TextArea rows={4} placeholder="Enter Notes" />
            </Form.Item>
          </Card>
        </Col>
      </Row>
    </Form>
  );
});

export default BranchSettingsS2;
